/**
 * Deal Stage Constants - SINGLE SOURCE OF TRUTH
 *
 * All stage probabilities and ordering are defined here.
 * DO NOT define stage probabilities anywhere else in the codebase.
 */

import { DealStage } from '../types/deal'

/**
 * Default probability percentages for each deal stage.
 * These are used when a deal doesn't have a custom probability set.
 *
 * Probabilities represent the likelihood of the deal closing successfully.
 */
export const STAGE_PROBABILITIES: Record<DealStage, number> = {
  Prospect: 10,
  Outreach: 20,
  Negotiating: 40,
  Agreed: 70,
  'Contract Sent': 85,
  Executed: 100,
  Lost: 0,
}

/**
 * Display order for deal stages in UI components.
 * Represents the natural progression of a deal through the pipeline.
 */
export const STAGE_ORDER: DealStage[] = [
  'Prospect',
  'Outreach',
  'Negotiating',
  'Agreed',
  'Contract Sent',
  'Executed',
  'Lost',
]

/**
 * Active pipeline stages - deals that are still in progress.
 * Excludes terminal states (Executed, Lost).
 */
export const ACTIVE_STAGES: DealStage[] = [
  'Prospect',
  'Outreach',
  'Negotiating',
  'Agreed',
  'Contract Sent',
]

/**
 * Terminal stages - deals that have reached a final state.
 */
export const TERMINAL_STAGES: DealStage[] = ['Executed', 'Lost']

/**
 * Won stage - successfully closed deals.
 */
export const WON_STAGE: DealStage = 'Executed'

/**
 * Lost stage - deals that did not close.
 */
export const LOST_STAGE: DealStage = 'Lost'

/**
 * Stage display colors — THE canonical stage palette.
 *
 * Reppr Elite tokens (see styles/themes.css): grey for early stages, the
 * brass ramp as a deal firms up, green won, red lost. chartTheme.ts and the
 * dashboard read from here — three competing stage palettes used to exist
 * and they disagreed with each other.
 *
 * Literal hexes (matching the token values) rather than var() because these
 * feed SVG chart attributes where custom properties are unreliable.
 */
export const STAGE_COLORS: Record<DealStage, string> = {
  Prospect: '#3A4149', //   --neutral-bar-dim
  Outreach: '#4A5561', //   --neutral-bar
  Negotiating: '#A8873F', //--accent-mid
  Agreed: '#C8A45C', //     --accent
  'Contract Sent': '#D8B570', // --accent-hover
  Executed: '#4E9A6A', //   --positive
  Lost: '#C4554D', //       --negative
}

/**
 * Stage labels for display (can be customized without changing type values).
 */
export const STAGE_LABELS: Record<DealStage, string> = {
  Prospect: 'Prospect',
  Outreach: 'Outreach',
  Negotiating: 'Negotiating',
  Agreed: 'Agreed',
  'Contract Sent': 'Contract Sent',
  Executed: 'Executed',
  Lost: 'Lost',
}
